// SendEmailRecipientsSection.jsx

import React from 'react';

// CSS
import './SendEmailRecipientsSection.css';

// Components
import Button from 'components/button/Button';
import ModalField from 'components/modal/field/ModalField';
import ModalLabel from 'components/modal/label/ModalLabel';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * SendEmailRecipientsSection
 *
 * Renders recipient-source-specific controls for send-email actions.
 *
 * @param {object} theme - Theme object used for dynamic styling.
 * @param {object} recipientSource - Normalized recipient source payload.
 * @param {array} [selectedRecipients=[]] - Resolved explicit-member recipient profiles.
 * @param {array} currentPeopleFieldOptions - People fields on the trigger record type.
 * @param {array} nodeTypeOptions - Base node types for related recipient selection.
 * @param {array} relatedPeopleFieldOptions - People fields from the selected related node type.
 * @param {function} onOpenRecipients - Opens explicit space member picker.
 * @param {function} onTriggerPeopleFieldChange - Updates trigger people field key.
 * @param {function} onRelatedNodeTypeChange - Updates related node type key.
 * @param {function} onRelatedPeopleFieldChange - Updates related people field key.
 *
 * @returns {ReactNode} The rendered recipient-source section.
 */
const SendEmailRecipientsSection = React.memo(({
  theme,
  recipientSource,
  selectedRecipients = [],
  currentPeopleFieldOptions,
  nodeTypeOptions,
  relatedPeopleFieldOptions,
  onOpenRecipients,
  onTriggerPeopleFieldChange,
  onRelatedNodeTypeChange,
  onRelatedPeopleFieldChange,
}) => {
  if (recipientSource.type === 'explicit_members') {
    const recipients = Array.isArray(selectedRecipients) ? selectedRecipients : [];

    return (
      <ModalField>

        {/* RECIPIENTS LIST */}
        {recipients.length > 0 && (
          <div className="automation-email-recipients-list">
            {recipients.map((recipient) => {
              const userKey = String(recipient?.userKey || '').trim();
              const name = String(recipient?.name || userKey).trim() || userKey;

              return (
                <div className="automation-email-recipient" key={userKey}>
                  <ProfilePhoto
                    node={{
                      key: userKey,
                      name,
                      title: name,
                      coverUrl: recipient?.coverUrl || null,
                    }}
                    size={20}
                    borderWidth={1}
                  />
                  <div
                    className="automation-email-recipient-name"
                    style={{
                      color: theme.foreground,
                    }}
                  >
                    {name}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Button
          label="Pick recipients"
          width="130px"
          title={recipients.length
            ? `${recipients.length} recipient${recipients.length === 1 ? '' : 's'} selected`
            : 'Pick recipients'}
          onClick={onOpenRecipients}
        />
      </ModalField>
    );
  }

  if (recipientSource.type === 'trigger_people_field') {
    return (
      <ModalField>
        <ModalLabel label="People field on this record" />
        <SelectInput
          style={{ fontSize: '12px' }}
          value={recipientSource.triggerPeopleFieldKey}
          placeholder="Select people field..."
          options={currentPeopleFieldOptions.map((field) => ({
            value: field.key,
            label: field.label,
          }))}
          onChange={(event) => {
            const nextFieldKey = String(event.target.value || '').trim();
            onTriggerPeopleFieldChange(nextFieldKey);
          }}
        />
      </ModalField>
    );
  }

  if (recipientSource.type === 'related_node_people_field') {
    return (
      <ModalField>
        <ModalLabel label="Related recipient field" />

        {/* RELATED GRID */}
        <div className="automation-email-related-grid">
          <SelectInput
            style={{ fontSize: '12px' }}
            value={recipientSource.relatedNodeTypeKey}
            placeholder="Related node type..."
            options={(Array.isArray(nodeTypeOptions) ? nodeTypeOptions : []).map((typeOption) => ({
              value: typeOption.key,
              label: typeOption.label || '',
            }))}
            onChange={(event) => {
              const nextTypeKey = String(event.target.value || '').trim();
              onRelatedNodeTypeChange(nextTypeKey);
            }}
          />

          <SelectInput
            style={{ fontSize: '12px' }}
            value={recipientSource.relatedPeopleFieldKey}
            placeholder="People field..."
            options={relatedPeopleFieldOptions.map((field) => ({
              value: field.key,
              label: field.label || '',
            }))}
            onChange={(event) => {
              const nextFieldKey = String(event.target.value || '').trim();
              onRelatedPeopleFieldChange(nextFieldKey);
            }}
          />
        </div>
      </ModalField>
    );
  }

  return null;
});

// Dev Tools
SendEmailRecipientsSection.displayName = 'SendEmailRecipientsSection';

export default SendEmailRecipientsSection;
